import moment, { Moment } from 'moment'
import React, { useEffect, useState } from 'react'
import { ConnectedProps, connect } from 'react-redux'
import { compose } from 'recompose'
import { change, getFormValues, isDirty as isDirtyForm } from 'redux-form'

import { DEFAULT_DATE_FORMAT } from 'constants/date'
import { Child } from 'services/child/constants'
import { UPLOAD_DIRECTORY } from 'services/legacy/upload/constants'
import { MF_SIGNATURE_TYPES } from 'services/medicationForms/constants'
import { Signature } from 'services/medicationForms/models'

import { usePrevious } from 'utils/hooks'

import { withAdministrationsService, withAdministrationsServiceProps } from 'services/administrations'
import { withCarersService, withCarersServiceProps } from 'services/legacy/carers'
import { withChildService, withChildServiceProps } from 'services/legacy/child'
import { withUploadService, withUploadServiceProps } from 'services/legacy/upload'
import { withModalService, withModalServiceProps } from 'services/utils/modal'

import { Option } from 'components/InfiniteDropdowns/InfiniteDropdownsModel'
import { ModalType } from 'modals'

import i18n from 'translations'

import AddEmergencyDoseView from './AddEmergencyDoseModalView'
import { ADD_EMERGENCY_DOSE_FORM, AddEmergencyDoseFormValues } from './components/AddEmergencyDoseModalForm'

const CHILD_GROUPS = {
  read: [
    'childInformation',
    'child.carerChildRelations',
    'carerChildRelation.carer',
    'carer',
    'carer.details',
  ],
}

export interface AddEmergencyDoseModalProps {
  childOption?: Option
  date?: Date | Moment | string | number
  isDiaryContext?: boolean
  onSubmitSuccess?: () => void
}

export type AddEmergencyDoseModalContainerProps = AddEmergencyDoseModalProps
  & withModalServiceProps
  & withAdministrationsServiceProps
  & withUploadServiceProps
  & withChildServiceProps
  & withCarersServiceProps

const mapState = (state) => ({
  formValues: getFormValues(ADD_EMERGENCY_DOSE_FORM)(state) as AddEmergencyDoseFormValues,
  isDirty: isDirtyForm(ADD_EMERGENCY_DOSE_FORM)(state),
})

const mapDispatch = {
  changeValue: (field, value) => change(ADD_EMERGENCY_DOSE_FORM, field, value),
}

const connector = connect(mapState, mapDispatch)

type PropsFromRedux = ConnectedProps<typeof connector>

const AddEmergencyDoseModalContainer: React.FC<AddEmergencyDoseModalContainerProps & PropsFromRedux> = ({
  administrationsActions,
  administrationsSelectors,
  changeValue,
  childActions,
  childOption,
  date,
  formValues,
  isDiaryContext,
  isDirty,
  modalActions,
  onSubmitSuccess,
  uploadActions,
}) => {
  const [isInitialize, setIsInitialized] = useState<boolean>(false)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isChildDetailLoading, setIsChildDetailLoading] = useState<boolean>(false)
  const [isResettingChild, setIsResettingChild] = useState<boolean>(false)
  const [childData, setChildData] = useState<Child>()

  const [initialValues, setInitialValues] = useState<AddEmergencyDoseFormValues>()

  const isChildContext = !!childOption

  const { child, takenAt } = formValues || {}
  const childValue = child?.value

  const previousTakenAt = usePrevious(takenAt)

  useEffect(() => {
    if (isInitialize && childValue) {
      setIsChildDetailLoading(true)

      childActions.get({
        onSuccess: ({ data }) => {
          setChildData(data)
          setIsChildDetailLoading(false)
          changeValue('parent', null)
        },
        onlyData: true,
        params: [childValue, {
          groups: CHILD_GROUPS,
        }],
      })
    }

    if (isInitialize && !childValue) {
      setChildData(null)
    }

    if (!isInitialize) {
      setIsInitialized(true)
    }
  }, [childValue])

  useEffect(() => {
    if (previousTakenAt
      && takenAt
      && moment(previousTakenAt).format(DEFAULT_DATE_FORMAT) !== moment(takenAt).format(DEFAULT_DATE_FORMAT)
    ) {
      setIsResettingChild(true)
      setChildData(null)
      changeValue('child', null)
      setTimeout(() => {
        setIsResettingChild(false)
      }, 1000)
    }
  }, [previousTakenAt, takenAt])

  useEffect(() => {
    setInitialValues({
      child: childOption,
      takenAt: moment(date),
      timeOfAdministration: moment(),
      wasParentContacted: false,
    })
  }, [childOption])

  const handleCancelClick = (isSaved?: boolean) => {
    if (isDirty && !isSaved) {
      modalActions.show<ModalType.CONFIRM>(ModalType.CONFIRM, {
        cancelButtonLabel: i18n.t('global:Continue'),
        confirmButtonLabel: i18n.t('global:Discard'),
        icon: 'warning',
        onConfirm: () => modalActions.hide(),
        text: i18n.t('module:Safeguarding:MedicationForm:Add:leavePopupMessage'),
      })

      return
    }

    modalActions.hide()
  }

  const handleSubmit = async (fields: AddEmergencyDoseFormValues) => {
    setIsSubmitting(true)

    const {
      administerSignature,
      administratedBy,
      witness,
      witnessSignature,
    } = fields

    const signatureBody: Signature[] = []

    signatureBody.push({
      owner: {
        id: administratedBy.value as number,
      },
      signature: {
        mimeType: administerSignature?.file?.type,
        size: administerSignature?.file?.size,
        url: await uploadActions.uploadFile(
          administerSignature?.file,
          administerSignature?.file.name,
          UPLOAD_DIRECTORY.IMAGES,
        ) as any,
      },
      type: MF_SIGNATURE_TYPES.STAFF,
    })

    signatureBody.push({
      owner: {
        id: witness.value as number,
      },
      signature: {
        mimeType: witnessSignature?.file?.type,
        size: witnessSignature?.file?.size,
        url: await uploadActions.uploadFile(
          witnessSignature?.file,
          witnessSignature?.file.name,
          UPLOAD_DIRECTORY.IMAGES,
        ) as any,
      },
      type: MF_SIGNATURE_TYPES.WITNESS,
    })

    const body = administrationsSelectors.getCreateBody(fields)

    administrationsActions.create({
      body: {
        ...body,
        signatures: signatureBody?.length ? signatureBody : undefined,
      },
      onFailed: () => {
        setIsSubmitting(false)
      },
      onSuccess: () => {
        onSubmitSuccess()
        setIsSubmitting(false)
        handleCancelClick(true)
      },
      params: [],
    })
  }

  return (
    <AddEmergencyDoseView
      child={childData}
      formValues={formValues}
      initialValues={initialValues}
      isChildContext={isChildContext}
      isChildDetailLoading={isChildDetailLoading}
      isDiaryContext={isDiaryContext}
      isResettingChild={isResettingChild}
      isSubmitting={isSubmitting}
      onCancelClick={() => handleCancelClick()}
      onSubmit={handleSubmit}
    />
  )
}

const enhance = compose(
  withModalService,
  withAdministrationsService,
  withUploadService,
  withChildService,
  withCarersService,
  connector,
)

export default enhance(AddEmergencyDoseModalContainer)

